// ── MenuPricingAdvisor — 메뉴 가격 결정 도우미 ─────────────────────
// 재료비 + 인건비 분담 + 임대/공과금 분담 → 종합 원가
// → 목표 마진율 기준 권장 판매가 + 실제 마진율
import React, { useState, useMemo } from 'react';
import { useLang } from '../i18n/LangContext';


const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// profitLoss에서 가장 최근 비용 데이터가 있는 월을 선택
function pickReferenceMonth(profitLoss) {
  if (!profitLoss || typeof profitLoss !== 'object') return null;
  const cur = getCurrentMonth();
  // 1순위: 현재월에 비용 있으면 그 월
  const curCosts = profitLoss[cur]?.costs;
  if (curCosts && (curCosts.labor || curCosts.rent || curCosts.utilities || curCosts.other)) {
    return cur;
  }
  // 2순위: 가장 최근 비용 있는 월
  const months = Object.keys(profitLoss).filter(k => /^\d{4}-\d{2}$/.test(k)).sort().reverse();
  for (const m of months) {
    const c = profitLoss[m]?.costs;
    if (c && (c.labor || c.rent || c.utilities || c.other)) return m;
  }
  return cur;  // 데이터 없으면 현재월 (입력 유도)
}

export default function MenuPricingAdvisor({
  materialCost = 0,           // 1인분 재료비
  sellingPrice = 0,           // 현재 판매가
  profitLoss = {},
  payrollSettings = {},
  onSettingsChange,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const { fmt: money, lang } = useLang();   // V109: 매장 통화 (원 / ¥) · V147 lang
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [targetMargin, setTargetMargin] = useState(60); // 목표 마진율 % (외식업 권장 60~70%)

  // 기준월 + 손익관리 비용 자동 로드
  const refMonth = useMemo(() => pickReferenceMonth(profitLoss), [profitLoss]);
  const refCosts = profitLoss?.[refMonth]?.costs || {};
  const monthlyLabor    = Number(refCosts.labor) || 0;
  const monthlyRent     = Number(refCosts.rent) || 0;
  const monthlyUtil     = Number(refCosts.utilities) || 0;
  const monthlyOther    = Number(refCosts.other) || 0;
  const monthlyFixed    = monthlyLabor + monthlyRent + monthlyUtil + monthlyOther;

  // 월 예상 총 판매수량 (shop-level, payrollSettings에 저장)
  const savedQty = Number(payrollSettings?.expectedMonthlyServings) || 0;
  const [localQty, setLocalQty] = useState(savedQty || 1000);
  const expectedQty = Number(localQty) || 0;

  // 인건비/고정비 분담
  const laborPerServing = expectedQty > 0 ? monthlyLabor / expectedQty : 0;
  const fixedPerServing = expectedQty > 0 ? (monthlyRent + monthlyUtil + monthlyOther) / expectedQty : 0;
  const totalCost = materialCost + laborPerServing + fixedPerServing;

  // 권장가: 목표 마진율을 만족하는 판매가
  // margin% = (price - cost) / price → price = cost / (1 - margin/100)
  const recommendedPrice = (targetMargin > 0 && targetMargin < 100)
    ? Math.round(totalCost / (1 - targetMargin / 100))
    : null;

  // 실제 마진율
  const realMargin = sellingPrice > 0 ? ((sellingPrice - totalCost) / sellingPrice) * 100 : null;
  const realProfit = sellingPrice > 0 ? (sellingPrice - totalCost) : null;

  // 마진율 색상
  const marginColor = realMargin === null ? '#9ca3af'
    : realMargin >= 60 ? '#FF6F0F'
    : realMargin >= 40 ? '#EA580C'
    : '#71717A';
  const marginLabel = realMargin === null ? Z('판매가 미입력', '未填售价')
    : realMargin >= 60 ? Z('✅ 우수', '✅ 很好')
    : realMargin >= 40 ? Z('⚠️ 평균', '⚠️ 一般')
    : realMargin >= 0 ? Z('🔴 낮음', '🔴 偏低')
    : Z('🚨 손실', '🚨 亏损');

  // 분담 데이터 누락 여부
  const hasFixedData = monthlyFixed > 0;
  const hasQtyData = expectedQty > 0;
  const ready = hasFixedData && hasQtyData;

  // 수량 입력 변경 (디바운스 없이 setSettings 호출)
  const updateQty = (n) => {
    setLocalQty(n);
    if (onSettingsChange) {
      onSettingsChange({ ...payrollSettings, expectedMonthlyServings: Number(n) || 0 });
    }
  };

  return (
    <div style={{
      marginTop: 16, background: '#fff', borderRadius: 12, padding: '14px 16px',
      border: '2px solid #A1A1AA', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', marginBottom: collapsed ? 0 : 12, flexWrap: 'wrap', gap: 6,
        }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#52525B' }}>
          🎯 {Z('종합 가격 결정', '综合定价')}
          <span style={{ fontSize: 11, fontWeight: 500, color: '#52525B', marginLeft: 8 }}>
            {Z('재료비 + 인건비 + 고정비 → 권장가', '食材费 + 人工费 + 固定费用 → 建议售价')}
          </span>
        </div>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {collapsed ? Z('펼치기 ▾', '展开 ▾') : Z('접기 ▴', '收起 ▴')}
        </span>
      </div>

      {!collapsed && (
        <>
          {/* 데이터 소스 표시 */}
          <div style={{
            background: '#FAFAFA', borderRadius: 8, padding: '8px 10px', marginBottom: 10,
            fontSize: 11, color: '#78350f', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            📊 {lang === 'zh' ? <>按 {refMonth ? `${refMonth.slice(0,4)}年${Number(refMonth.slice(5))}月` : '本月'} <strong>运营</strong> 数据</> : <>{refMonth ? `${refMonth.slice(0,4)}년 ${refMonth.slice(5)}월` : '현재월'} <strong>손익관리</strong> 데이터 기준</>}
            {!hasFixedData && (
              <span style={{ color: '#52525B', fontWeight: 700 }}>
                {Z('— 손익관리에 인건비·임대료·공과금을 입력하세요', '— 请在运营里填写人工费 · 租金 · 水电费')}
              </span>
            )}
          </div>

          {/* 1인분 원가 분담 카드 */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 8, marginBottom: 10,
          }}>
            <CostCard label={Z('재료비', '食材费')} value={materialCost} color="#FF6F0F" auto />
            <CostCard label={Z('인건비 분담', '分摊人工费')} value={laborPerServing} color="#1A56A0"
                      missing={!hasFixedData || !hasQtyData} />
            <CostCard label={Z('고정비 분담', '分摊固定费用')} value={fixedPerServing} color="#7C3AED"
                      sub={Z('임대+공과+기타', '租金 + 水电 + 其他')}
                      missing={!hasFixedData || !hasQtyData} />
            <CostCard label={Z('종합 원가', '综合成本')} value={totalCost} color="#52525B" big
                      missing={!ready} />
          </div>

          {/* 월 예상 총 판매수량 입력 */}
          <div style={{
            background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 10,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <label style={{ fontSize: 12, color: '#374151', fontWeight: 700, flexShrink: 0 }}>
              {Z('월 예상 총 판매수량', '每月预计总销量')}
            </label>
            <input
              type="number"
              value={localQty}
              onChange={e => updateQty(e.target.value)}
              min="1"
              step="100"
              placeholder={Z('예: 1000', '例：1000')}
              style={{
                flex: 1, minWidth: 100, padding: '6px 10px',
                border: '1.5px solid #FF6F0F', borderRadius: 6,
                fontSize: 13, textAlign: 'right', fontWeight: 700,
              }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>{Z('개 / 월', '份 / 月')}</span>
            <span style={{ fontSize: 10, color: '#9ca3af', flexBasis: '100%', marginTop: 2 }}>
              {Z('💡 매장 전체 메뉴 합산 예상치 (모든 메뉴에 동일하게 적용 — 매장 설정에 저장)', '💡 全店所有菜品合计的预计销量（所有菜品共用，保存在门店设置里）')}
            </span>
          </div>

          {/* 목표 마진율 → 권장 판매가 */}
          <div style={{
            background: ready ? '#FAFAFA' : '#f8fafc',
            borderRadius: 8, padding: '12px 14px', marginBottom: 10,
            border: ready ? '1.5px solid #FF6F0F' : '1px solid #e5e7eb',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: '#374151', fontWeight: 700 }}>
                {Z('목표 마진율', '目标利润率')}
              </label>
              <input
                type="number"
                value={targetMargin}
                onChange={e => setTargetMargin(Number(e.target.value) || 0)}
                min="0" max="95" step="1"
                style={{
                  width: 60, padding: '6px 8px', border: '1.5px solid #FF6F0F',
                  borderRadius: 6, fontSize: 13, textAlign: 'right', fontWeight: 700,
                }}
              />
              <span style={{ fontSize: 13, color: '#6b7280' }}>%</span>
              <div style={{ flex: 1, minWidth: 140, textAlign: 'right' }}>
                <span style={{ fontSize: 11, color: '#6b7280', marginRight: 6 }}>{Z('권장 판매가', '建议售价')}</span>
                <strong style={{ fontSize: 18, color: '#18181B' }}>
                  {ready && recommendedPrice !== null ? money(recommendedPrice) : '-'}
                </strong>
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>
              {Z('💡 외식업은 보통 마진율 60~70%를 권장 (손익분기 + 적정 이익)', '💡 餐饮业一般建议利润率 60~70%（保本 + 合理利润）')}
            </div>
          </div>

          {/* 현재 판매가 vs 종합 원가 — 실제 마진율 */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 8, marginBottom: 8,
          }}>
            <SummaryBox label={Z('현재 판매가', '现在售价')} value={sellingPrice > 0 ? money(sellingPrice) : Z('미입력', '未填')}
                        color={sellingPrice > 0 ? '#1a1a1a' : '#9ca3af'} />
            <SummaryBox label={Z('종합 원가', '综合成本')} value={ready ? money(totalCost) : '-'}
                        color="#52525B" />
            <SummaryBox label={Z('실제 이익', '实际利润')} value={realProfit !== null && ready ? money(realProfit) : '-'}
                        color={realProfit !== null && realProfit > 0 ? '#FF6F0F' : '#71717A'} />
            <SummaryBox label={Z('실제 마진율', '实际利润率')}
                        value={realMargin !== null && ready ? `${realMargin.toFixed(1)}%` : '-'}
                        sub={ready ? marginLabel : ''}
                        color={marginColor}
                        big />
          </div>

          {/* 안내 */}
          {!hasFixedData && (
            <div style={{
              padding: '8px 10px', borderRadius: 7,
              background: '#fff1f2', border: '1px solid #fca5a5',
              fontSize: 11, color: '#b91c1c', fontWeight: 600,
            }}>
              {Z('⚠️ 손익관리 → 비용 항목(인건비·임대료·공과금)을 먼저 입력하세요. 인건비 모듈에서 "💰 손익관리에 자동 반영" 사용 가능.', '⚠️ 请先在 运营 → 成本 里填写人工费 · 租金 · 水电费。')}
            </div>
          )}
          {hasFixedData && !hasQtyData && (
            <div style={{
              padding: '8px 10px', borderRadius: 7,
              background: '#fff1f2', border: '1px solid #fca5a5',
              fontSize: 11, color: '#b91c1c', fontWeight: 600,
            }}>
              {Z('⚠️ 월 예상 총 판매수량을 1 이상 입력하세요.', '⚠️ 请填写每月预计总销量（1 以上）。')}
            </div>
          )}
          {ready && realMargin !== null && realMargin < 30 && (
            <div style={{
              padding: '8px 10px', borderRadius: 7,
              background: '#fff1f2', border: '1px solid #fca5a5',
              fontSize: 11, color: '#b91c1c', fontWeight: 700,
            }}>
              {Z(`🚨 마진율이 매우 낮습니다. 판매가를 권장가(${money(recommendedPrice)}) 수준으로 올리거나, 재료비를 줄이는 검토가 필요합니다.`, `🚨 利润率很低。可以考虑把售价调到建议售价（${money(recommendedPrice)}）左右，或降低食材费。`)}
            </div>
          )}
          {ready && realMargin !== null && realMargin >= 30 && realMargin < 50 && (
            <div style={{
              padding: '8px 10px', borderRadius: 7,
              background: '#FAFAFA', border: '1px solid #D4D4D8',
              fontSize: 11, color: '#52525B', fontWeight: 600,
            }}>
              {Z('⚠️ 마진율이 평균 이하입니다. 인건비·고정비 부담이 크면 판매가 조정을 검토하세요.', '⚠️ 利润率低于平均。人工和固定费用负担大的话，可以考虑调整售价。')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── 원가 분담 카드
function CostCard({ label, value, color, sub, big, missing, auto }) {
  const { fmt: money, lang } = useLang();
  return (
    <div style={{
      background: missing ? '#f9fafb' : '#fff',
      border: `1.5px solid ${missing ? '#e5e7eb' : color}`,
      borderRadius: 8, padding: '10px 12px',
      opacity: missing ? 0.55 : 1,
    }}>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {auto && <span style={{ fontSize: 9, color: '#18181B' }}>{lang === 'zh' ? '自动' : '자동'}</span>}
      </div>
      <div style={{
        fontSize: big ? 16 : 14, fontWeight: 800,
        color: missing ? '#9ca3af' : color,
      }}>
        {missing ? '—' : money(value)}
      </div>
      {sub && (
        <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

// ── 요약 박스
function SummaryBox({ label, value, sub, color, big }) {
  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e5e7eb',
      borderRadius: 8, padding: '10px 12px',
    }}>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3 }}>{label}</div>
      <div style={{
        fontSize: big ? 17 : 14, fontWeight: 800, color,
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 10, color, marginTop: 2, fontWeight: 600 }}>{sub}</div>
      )}
    </div>
  );
}
